/**
 * The commission-bearing rows, grouped as a tree: event → game bucket → market
 * → the settled bet itself.
 *
 * The flat table this replaces was one row per settled bet, which at a month's
 * range is several hundred rows of mostly the same event repeated. The question
 * an agent arrives with is "which events cost me commission, and what inside
 * them" — four levels answer that by opening, in the order the question is
 * actually asked, and the leaf is still the settled row, so the member and the
 * settled time never left.
 *
 * `game` is the bucket, deliberately the same field the "Commission by game"
 * donut on this page groups by. Two groupings of the same money on one screen
 * would be two answers to one question.
 *
 * ## The one invariant
 *
 * A parent shows EXACTLY the sum of the children beneath it, at every level.
 * Every figure is accumulated bottom-up from already-rounded children rather
 * than recomputed from the raw leaves per level — recomputing lets a branch land
 * a paisa away from the rows printed under it, and a branch reading ₹1,840 over
 * children reading 980 + 860 sends an operator hunting for a rupee that was
 * never missing. `commissionTree.test.ts` pins this.
 */

import { round2 } from '@/lib/book'
import type { PnlRow } from '@/data/types'

/** Root first. A `bet` is always a leaf; the other three always have children. */
export type CommissionNodeKind = 'event' | 'game' | 'market' | 'bet'

export interface CommissionNode {
  /** Unique across the WHOLE tree, not just among siblings — see `KEY_SEP`. */
  key: string
  kind: CommissionNodeKind
  /** What the tree column renders: event, bucket, market, or the member. */
  label: string
  /** Rolled up from the children, or the row's own figure on a leaf. */
  commission: number
  memberPnl: number
  /** The book's side of `netAmount` — negated, then summed. */
  netToBook: number
  betCount: number
  /**
   * Earliest settlement beneath this node, ISO.
   *
   * Carried mainly for the event roots: the same fixture name comes round every
   * few days, so a root reading only "Ascot 15:40" leaves the operator unable to
   * tell Tuesday's card from Friday's.
   */
  at: string
  /** The settled row, on `bet` leaves only; null on every branch. */
  row: PnlRow | null
  children: CommissionNode[]
}

export interface CommissionTotals {
  commission: number
  memberPnl: number
  netToBook: number
  betCount: number
}

/**
 * Unit Separator. Keys are path-shaped, so they need a delimiter no event or
 * market name can contain — with an ordinary character like `|`, an event
 * literally named "A|B" would forge the key of bucket B under event A and the
 * two would share expansion state.
 */
const KEY_SEP = '\u001f'

const ZERO: CommissionTotals = { commission: 0, memberPnl: 0, netToBook: 0, betCount: 0 }

/**
 * Sum children into a parent.
 *
 * `round2` lands on the accumulated sum of values that are already rounded, so
 * it only ever removes float noise (0.1 + 0.2) and never re-derives a figure the
 * children disagree with.
 */
function rollUp(children: CommissionNode[]): CommissionTotals {
  const acc = children.reduce(
    (sum, child) => ({
      commission: sum.commission + child.commission,
      memberPnl: sum.memberPnl + child.memberPnl,
      netToBook: sum.netToBook + child.netToBook,
      betCount: sum.betCount + child.betCount,
    }),
    ZERO,
  )
  return {
    commission: round2(acc.commission),
    memberPnl: round2(acc.memberPnl),
    netToBook: round2(acc.netToBook),
    betCount: acc.betCount,
  }
}

/**
 * Biggest commission first, then by label.
 *
 * The label tiebreak is not cosmetic: this list is rebuilt on every refetch, and
 * leaving equal-commission siblings to sort stability lets rows swap places on
 * identical data, which reads as the table losing its place.
 */
function byCommission(a: CommissionNode, b: CommissionNode): number {
  return b.commission - a.commission || a.label.localeCompare(b.label) || a.key.localeCompare(b.key)
}

function branch(
  key: string,
  kind: CommissionNodeKind,
  label: string,
  children: CommissionNode[],
): CommissionNode {
  const at = children.reduce((min, c) => (c.at < min ? c.at : min), children[0]?.at ?? '')
  children.sort(byCommission)
  return { key, kind, label, at, ...rollUp(children), row: null, children }
}

/**
 * A bet leaf is labelled by its member — the one thing the row adds over its
 * market.
 *
 * `PnlRow` carries `memberId` and no username, so the name comes from a resolver
 * the page supplies. Falling back to the raw id keeps the label honest: an
 * unresolved member reads as an identifier rather than as a blank cell that
 * looks like the row has no owner.
 */
function leaf(parentKey: string, row: PnlRow, memberName: (id: string) => string): CommissionNode {
  return {
    key: `${parentKey}${KEY_SEP}b:${row.id}`,
    kind: 'bet',
    label: memberName(row.memberId),
    at: row.settledAt,
    /* Rounded HERE, once, even though every `PnlRow` the fixture and the mart
       produce is already exact to the paisa. It is the leaf that anchors the
       invariant: if a future source ever hands us three decimals, a branch
       summing rounded children would sit a fraction off the unrounded rows
       printed beneath it, and the tree would be quietly wrong rather than
       loudly broken. */
    commission: round2(row.commission),
    memberPnl: round2(row.memberPnl),
    netToBook: round2(-row.netAmount),
    betCount: row.betCount,
    row,
    children: [],
  }
}

/**
 * A row survives the text filter when its EVENT or its MARKET matches.
 *
 * Matching on the event keeps that event's every market, which is what someone
 * typing a fixture name wants; matching on a market narrows to it. Both fall out
 * of filtering rows before grouping rather than pruning the built tree.
 */
function matches(row: PnlRow, needle: string): boolean {
  return (
    row.eventName.toLowerCase().includes(needle) || row.marketName.toLowerCase().includes(needle)
  )
}

/**
 * The rows the filter box leaves standing.
 *
 * Exported because the CSV export has to write exactly the rows the tree is
 * showing. Two implementations of "what the filter means" would drift, and the
 * one that drifts is always the export, because nobody is looking at it.
 */
export function filterCommissionRows(rows: readonly PnlRow[], needle?: string): PnlRow[] {
  const q = (needle ?? '').trim().toLowerCase()
  return q ? rows.filter((row) => matches(row, q)) : [...rows]
}

/** Group preserving first-seen order; the sort comes after roll-up, on value. */
function groupBy<T>(items: readonly T[], key: (item: T) => string): Map<string, T[]> {
  const out = new Map<string, T[]>()
  for (const item of items) {
    const k = key(item)
    const bucket = out.get(k)
    if (bucket) bucket.push(item)
    else out.set(k, [item])
  }
  return out
}

/**
 * Build the roots.
 *
 * `needle` is the filter bar's free text — undefined, empty or whitespace means
 * no filter. `memberName` resolves a bet leaf's label; omit it and leaves are
 * labelled by member id.
 */
export function buildCommissionTree(
  rows: readonly PnlRow[],
  needle?: string,
  memberName: (id: string) => string = (id) => id,
): CommissionNode[] {
  const kept = filterCommissionRows(rows, needle)

  /* Grouped on `eventId`, labelled with `eventName`. The same fixture name
     recurs every few days, so grouping on the name would add a month of
     different matches into one root and print one match's name over the total. */
  const events = [...groupBy(kept, (r) => r.eventId)].map(([eventId, eventRows]) => {
    const eventKey = `e:${eventId}`
    const eventName = eventRows[0]!.eventName

    const games = [...groupBy(eventRows, (r) => r.game)].map(([game, gameRows]) => {
      const gameKey = `${eventKey}${KEY_SEP}g:${game}`

      const markets = [...groupBy(gameRows, (r) => r.marketName)].map(([marketName, marketRows]) => {
        const marketKey = `${gameKey}${KEY_SEP}m:${marketName}`
        return branch(
          marketKey,
          'market',
          marketName,
          marketRows.map((row) => leaf(marketKey, row, memberName)),
        )
      })

      return branch(gameKey, 'game', game, markets)
    })

    return branch(eventKey, 'event', eventName, games)
  })

  events.sort(byCommission)
  return events
}

/**
 * `childrenOf` for `TreeGrid`, as a flat map.
 *
 * The whole tree is already in memory — these rows came from one query — so
 * there is nothing to lazily fetch and a leaf must answer `undefined` rather
 * than an empty array, which the grid reads as "loaded, and empty".
 */
export function childIndex(roots: readonly CommissionNode[]): Map<string, CommissionNode[]> {
  const index = new Map<string, CommissionNode[]>()
  const visit = (nodes: readonly CommissionNode[]) => {
    for (const node of nodes) {
      if (node.children.length === 0) continue
      index.set(node.key, node.children)
      visit(node.children)
    }
  }
  visit(roots)
  return index
}

/** The totals strip — the roots rolled up the same way every branch is. */
export function treeTotals(roots: readonly CommissionNode[]): CommissionTotals {
  return rollUp([...roots])
}
